import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, InputNumber, Form, Input, message, Spin, Modal, Tag, Select } from 'antd';
import { ShoppingCartOutlined, ArrowLeftOutlined, CopyOutlined } from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import { productAPI, orderAPI, cardAPI } from '../utils/api';
import './ProductDetail.css';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('usdt'); // 默认USDT支付
  const [countdown, setCountdown] = useState(300); // 5分钟倒计时
  const [txHash, setTxHash] = useState(''); // 交易哈希
  const [verifying, setVerifying] = useState(false); // 验证中状态
  const [form] = Form.useForm();

  useEffect(() => {
    loadProduct();
  }, [id]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      const product = await productAPI.getById(id);
      if (product) {
        // 获取实时库存（可用卡密数量）
        const cards = await cardAPI.getByProduct(id, true);
        product.stock = cards.length;
        setProduct(product);
      } else {
        message.error('商品不存在');
        navigate('/');
      }
    } catch (error) {
      message.error('加载商品失败');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    if (product.stock < values.quantity) {
      message.error('库存不足');
      return;
    }

    try {
      setSubmitting(true);
      const totalAmount = product.price * values.quantity;
      
      // 创建订单
      const order = await orderAPI.create(
        product.id, 
        totalAmount, 
        values.quantity,
        values.email || '',
        paymentMethod
      );
      
      if (paymentMethod === 'usdt') {
        // USDT支付
        const paymentResult = await orderAPI.createUsdtPayment({
          orderNo: order.orderNo,
          totalAmount: totalAmount
        });
        
        if (paymentResult.success) {
          // 如果金额被调整，显示提示
          if (paymentResult.adjustedCny) {
            message.warning(`为避免金额冲突，订单金额已调整为 ¥${paymentResult.adjustedCny}`);
          }
          
          setPaymentData({
            orderNo: order.orderNo,
            totalPrice: paymentResult.adjustedCny || totalAmount.toFixed(2),
            paymentMethod: 'USDT',
            usdtAmount: paymentResult.amount,
            address: paymentResult.address,
            chain: paymentResult.chain,
            qrCode: paymentResult.address, // 收款地址作为二维码内容
            adjusted: !!paymentResult.adjustedCny
          });
          
          setPaymentModal(true);
          message.success('订单创建成功！请转账USDT到指定地址');
          
          // 开始轮询支付状态
          startPollingPayment(order.orderNo);
        } else {
          message.error('创建支付订单失败：' + (paymentResult.error || '未知错误'));
        }
      } else {
        // 微信支付
        const paymentResult = await orderAPI.createWechatPayment({
          orderNo: order.orderNo,
          totalAmount: totalAmount,
          description: `购买${product.name} x ${values.quantity}`
        });
        
        if (paymentResult.success) {
          setPaymentData({
            orderNo: order.orderNo,
            totalPrice: totalAmount.toFixed(2),
            paymentMethod: '微信',
            qrCode: paymentResult.codeUrl
          });
          
          setPaymentModal(true);
          message.success('订单创建成功！请使用微信扫码支付');
          
          // 开始轮询支付状态
          startPollingPayment(order.orderNo);
        } else {
          message.error('创建支付订单失败：' + (paymentResult.error || '未知错误'));
        }
      }
    } catch (error) {
      message.error(error.message || '创建订单失败');
    } finally {
      setSubmitting(false);
    }
  };

  const startPollingPayment = (orderNo) => {
    // 重置倒计时
    setCountdown(300);
    
    // 倒计时器
    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    // 支付状态轮询
    const interval = setInterval(async () => {
      try {
        const orders = await orderAPI.checkOrderStatus(orderNo);
        if (orders && orders.length > 0 && orders[0].status === 'paid') {
          clearInterval(interval);
          clearInterval(countdownInterval);
          setPaymentModal(false);
          
          const cardCode = orders[0].deliveryInfo || orders[0].cardCode;
          Modal.success({
            title: '🎉 支付成功',
            width: 500,
            content: (
              <div style={{ padding: '20px 0' }}>
                <p style={{ fontSize: '16px', marginBottom: '20px' }}>订单已完成支付！</p>
                {cardCode ? (
                  <div style={{ 
                    background: '#f6ffed', 
                    border: '2px solid #b7eb8f',
                    borderRadius: '8px',
                    padding: '20px',
                    marginTop: '16px'
                  }}>
                    <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#52c41a', fontWeight: 'bold' }}>🎁 您的卡密:</p>
                    <div style={{
                      background: '#fff',
                      padding: '12px',
                      borderRadius: '4px',
                      fontFamily: 'monospace',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      wordBreak: 'break-all',
                      color: '#262626',
                      userSelect: 'all'
                    }}>
                      {cardCode}
                    </div>
                    <Button 
                      type="link" 
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => {
                        navigator.clipboard.writeText(cardCode);
                        message.success('卡密已复制到剪贴板');
                      }}
                      style={{ marginTop: '8px', padding: 0 }}
                    >
                      点击复制卡密
                    </Button>
                  </div>
                ) : (
                  <p style={{ color: '#faad14' }}>⏳ 卡密正在处理中，请稍后在"我的订单"中查看</p>
                )}
              </div>
            ),
            okText: '查看我的订单',
            onOk: () => navigate('/orders')
          });
        }
      } catch (error) {
        console.error('查询支付状态失败:', error);
      }
    }, 3000);
    
    // 5分钟后停止轮询
    setTimeout(() => {
      clearInterval(interval);
      clearInterval(countdownInterval);
    }, 300000);
  };

  // 支付轮询功能暂时禁用（等待支付接口完善）
  // const startPollingPayment = (orderNo) => {
  //   const interval = setInterval(async () => {
  //     try {
  //       const result = await orderAPI.checkStatus(orderNo);
  //       if (result && result.paymentStatus === 'paid') {
  //         clearInterval(interval);
  //         Modal.success({
  //           title: '支付成功',
  //           content: '订单已完成，请前往我的订单查看',
  //           okText: '查看订单',
  //           onOk: () => navigate('/my-orders')
  //         });
  //       }
  //     } catch (error) {
  //       console.error('查询支付状态失败:', error);
  //     }
  //   }, 3000);
  //   setTimeout(() => clearInterval(interval), 300000);
  // };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!product) {
    return null;
  }

  return (
    <div className="product-detail-page">
      <Button 
        icon={<ArrowLeftOutlined />} 
        onClick={() => navigate(-1)}
        style={{ marginBottom: 16 }}
      >
        返回
      </Button>

      <Card>
        <div className="product-detail-content">
          <div className="product-image-section">
            {product.image ? (
              <img src={product.image} alt={product.name} />
            ) : (
              <div className="no-image">
                <ShoppingCartOutlined style={{ fontSize: 80 }} />
              </div>
            )}
          </div>

          <div className="product-info-section">
            <h1>{product.name}</h1>
            <div className="product-meta">
              <Tag color={product.stock > 0 ? 'success' : 'error'}>
                库存: {product.stock}
              </Tag>
              <Tag color="blue">已售: {product.soldCount}</Tag>
              {product.category && <Tag>{product.category}</Tag>}
            </div>
            <div className="product-price">¥{product.price}</div>
            <div className="product-description">
              <h3>商品描述</h3>
              <p>{product.description || '暂无描述'}</p>
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              initialValues={{ quantity: 1 }}
            >
              <Form.Item
                label="购买数量"
                name="quantity"
                rules={[{ required: true, message: '请输入购买数量' }]}
              >
                <InputNumber
                  min={1}
                  max={product.stock}
                  style={{ width: '100%' }}
                />
              </Form.Item>

              <Form.Item
                label="联系邮箱（选填）"
                name="email"
                rules={[{ type: 'email', message: '请输入有效的邮箱地址' }]}
              >
                <Input placeholder="用于接收订单信息" />
              </Form.Item>

              <Form.Item label="支付方式">
                <Select value={paymentMethod} onChange={setPaymentMethod} size="large">
                  <Select.Option value="usdt">USDT (BSC链) - 推荐</Select.Option>
                  <Select.Option value="wechat">微信支付</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  block
                  loading={submitting}
                  disabled={product.stock === 0}
                  icon={<ShoppingCartOutlined />}
                >
                  {product.stock > 0 ? '立即购买' : '库存不足'}
                </Button>
              </Form.Item>
            </Form>
          </div>
        </div>
      </Card>

      <Modal
        title={paymentData?.paymentMethod === 'USDT' ? 'USDT支付' : '扫码支付'}
        open={paymentModal}
        footer={null}
        onCancel={() => setPaymentModal(false)}
        width={450}
      >
        {paymentData && (
          <div style={{ textAlign: 'center' }}>
            {countdown > 0 && (
              <div style={{ 
                background: '#fff7e6', 
                border: '1px solid #ffd591',
                borderRadius: '4px',
                padding: '12px',
                marginBottom: '16px',
                fontSize: '14px',
                color: '#d46b08'
              }}>
                ⏰ 请在 <strong style={{ fontSize: '16px', color: '#fa8c16' }}>{Math.floor(countdown / 60)}:{(countdown % 60).toString().padStart(2, '0')}</strong> 内完成支付
              </div>
            )}
            <div style={{ marginBottom: 16 }}>
              <QRCodeSVG value={paymentData.qrCode} size={256} />
            </div>
            
            {paymentData.paymentMethod === 'USDT' ? (
              <>
                <p><strong>订单号:</strong> {paymentData.orderNo}</p>
                <p><strong>支付金额:</strong> <span style={{ color: '#ff4d4f', fontSize: '16px', fontWeight: 'bold' }}>{paymentData.usdtAmount} USDT</span> <span style={{ color: '#999', fontSize: '13px' }}>(≈¥{paymentData.totalPrice})</span></p>
                {paymentData.adjusted && (
                  <p style={{ color: '#fa8c16', fontSize: '12px', background: '#fff7e6', padding: '8px', borderRadius: '4px', marginBottom: '8px' }}>
                    💡 金额已自动微调以避免冲突
                  </p>
                )}
                <p><strong>区块链:</strong> {paymentData.chain}</p>
                <div style={{ 
                  background: '#f5f5f5', 
                  padding: '12px', 
                  borderRadius: '4px', 
                  marginTop: '16px',
                  wordBreak: 'break-all'
                }}>
                  <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>收款地址:</p>
                  <p style={{ margin: '8px 0', fontFamily: 'monospace', fontSize: '13px' }}>
                    {paymentData.address}
                  </p>
                  <Button 
                    size="small" 
                    icon={<CopyOutlined />}
                    onClick={() => {
                      navigator.clipboard.writeText(paymentData.address);
                      message.success('地址已复制');
                    }}
                  >
                    复制地址
                  </Button>
                </div>
                <p style={{ color: '#ff4d4f', fontSize: 12, marginTop: 16 }}>
                  ⚠️ 请务必通过BSC链转账USDT，其他链或币种将无法到账！
                </p>
                <p style={{ color: '#666', fontSize: 12 }}>
                  转账成功后，系统将自动检测并发货（通常1-5分钟）
                </p>
                
                {/* 手动提交交易哈希 */}
                <div style={{ 
                  background: '#f0f5ff', 
                  padding: '16px', 
                  borderRadius: '4px', 
                  marginTop: '20px',
                  border: '1px solid #d6e4ff'
                }}>
                  <p style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 'bold', color: '#1890ff' }}>
                    🚀 已转账？立即验证发货
                  </p>
                  <Input
                    placeholder="请输入交易哈希(TxHash)，例如：0x123..."
                    value={txHash}
                    onChange={(e) => setTxHash(e.target.value)}
                    style={{ marginBottom: '8px' }}
                    disabled={verifying}
                  />
                  <Button
                    type="primary"
                    block
                    loading={verifying}
                    disabled={!txHash || txHash.length < 10}
                    onClick={async () => {
                      try {
                        setVerifying(true);
                        const result = await orderAPI.checkUsdtPayment(paymentData.orderNo, txHash);
                        
                        if (result.paid) {
                          setPaymentModal(false);
                          
                          const cardCode = result.cardCode || result.deliveryInfo;
                          Modal.success({
                            title: '🎉 支付验证成功',
                            width: 500,
                            content: (
                              <div style={{ padding: '20px 0' }}>
                                <p style={{ fontSize: '16px', marginBottom: '20px' }}>订单已完成支付！</p>
                                {cardCode ? (
                                  <div style={{ 
                                    background: '#f6ffed', 
                                    border: '2px solid #b7eb8f',
                                    borderRadius: '8px',
                                    padding: '20px',
                                    marginTop: '16px'
                                  }}>
                                    <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#52c41a', fontWeight: 'bold' }}>🎁 您的卡密:</p>
                                    <div style={{
                                      background: '#fff',
                                      padding: '12px',
                                      borderRadius: '4px',
                                      fontFamily: 'monospace',
                                      fontSize: '16px',
                                      fontWeight: 'bold',
                                      wordBreak: 'break-all',
                                      color: '#262626',
                                      userSelect: 'all'
                                    }}>
                                      {cardCode}
                                    </div>
                                    <Button 
                                      type="link" 
                                      size="small"
                                      icon={<CopyOutlined />}
                                      onClick={() => {
                                        navigator.clipboard.writeText(cardCode);
                                        message.success('卡密已复制到剪贴板');
                                      }}
                                      style={{ marginTop: '8px', padding: 0 }}
                                    >
                                      点击复制卡密
                                    </Button>
                                  </div>
                                ) : (
                                  <p style={{ color: '#faad14' }}>⏳ 卡密正在处理中，请稍后在"我的订单"中查看</p>
                                )}
                              </div>
                            ),
                            okText: '查看我的订单',
                            onOk: () => navigate('/orders')
                          });
                        } else {
                          message.error('交易验证失败，请检查交易哈希是否正确');
                        }
                      } catch (error) {
                        message.error(error.message || '验证失败，请稍后重试');
                      } finally {
                        setVerifying(false);
                      }
                    }}
                  >
                    {verifying ? '验证中...' : '提交验证并发货'}
                  </Button>
                  <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
                    💡 提示：在钱包转账记录中复制交易哈希，粘贴到上方输入框
                  </p>
                </div>
              </>
            ) : (
              <>
                <p>订单号: {paymentData.orderNo}</p>
                <p>金额: ¥{paymentData.totalPrice}</p>
                <p style={{ color: '#666', fontSize: 12 }}>
                  请使用微信扫码支付，支付成功后自动发货
                </p>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ProductDetail;
